<template>
  <!--
    CreateCase.vue - Hovedkomponent for ny sagoprettelse
    =====================================================
    NY WIZARD STRUKTUR med 3-4 steps og AI-kategorisering.

    Steps:
    1. Grundlag - Upload alle dokumenter, AI kategoriserer
    2. Skyldner - AI pre-udfylder, bruger verificerer
    3. Ekstra information - Kun hvis påkrævede custom variables (skjult ellers)
    4. Bekræftelse - Samlet overblik før afsendelse

    Features:
    - Én drop-zone for alle dokumenter
    - AI kategorisering med drag-and-drop mellem kategorier
    - Claude Vision OCR
    - CVR-opslag og debtor matching
    - Debug panel for udvikling
  -->
  <div class="create-case-container">
    <!-- Case Summary Bar - Live oversigt over sagens økonomi -->
    <CaseSummaryBar v-if="hasAnyDocuments" />

    <!-- Step Indicator - Viser progress gennem wizarden -->
    <StepIndicator
      :steps="STEPS"
      :current-step="state.currentStep"
      :has-custom-variables="hasRequiredCustomVariables"
      @step-click="handleStepClick"
    />

    <!-- Alert for fejlbeskeder -->
    <b-alert
      v-if="state.error"
      variant="danger"
      show
      dismissible
      class="mb-2"
      @dismissed="state.error = null"
    >
      <div class="alert-body">
        <feather-icon
          icon="AlertTriangleIcon"
          class="mr-50"
        />
        {{ state.error }}
      </div>
    </b-alert>

    <!-- Hoved-indhold: Step komponenter -->
    <b-card class="step-content-card">
        <!-- Step 1: Grundlag (NY - alle dokumenter samlet) -->
        <StepGrundlag v-if="state.currentStep === 1" />

        <!-- Step 2: Skyldner (NY - med AI pre-fill) -->
        <StepSkyldner
          v-if="state.currentStep === 2"
          :debtors="state.debtors"
          :debtor-types="DEBTOR_TYPES"
          :client-id="clientId"
          @add-debtor="addDebtor"
          @remove-debtor="removeDebtor"
          @update-debtor="handleDebtorUpdate"
        />

        <!-- Step 3: Ekstra Information (kun hvis påkrævede custom vars) -->
        <StepCustomVariables
          v-if="state.currentStep === 3 && hasRequiredCustomVariables"
          :definitions="state.customVariables.definitions"
          :values="state.customVariables.values"
          @update-value="handleCustomVariableUpdate"
        />

        <!-- Step 4: Bekræftelse -->
        <StepConfirmation
          v-if="state.currentStep === 4"
          :case-data="state"
          :case-types="CASE_TYPES"
          :debtor-types="DEBTOR_TYPES"
        />
      </b-card>

    <!-- Navigation knapper - Skjult på Step 1 indtil der er uploadet dokumenter -->
    <b-card
      v-if="state.currentStep > 1 || hasAnyDocuments"
      class="mt-2"
    >
      <b-row>
        <b-col cols="6">
          <b-button
            v-if="state.currentStep > 1"
            variant="outline-secondary"
            @click="prevStep"
          >
            <feather-icon
              icon="ChevronLeftIcon"
              class="mr-25"
            />
            {{ $t('DEBTIA_PREVIOUS') }}
          </b-button>
        </b-col>
        <b-col
          cols="6"
          class="text-right"
        >
          <!-- Næste knap (ikke på sidste step) -->
          <b-button
            v-if="state.currentStep < lastStep"
            variant="primary"
            :disabled="!isCurrentStepValid"
            @click="handleNextStep"
          >
            {{ $t('DEBTIA_NEXT') }}
            <feather-icon
              icon="ChevronRightIcon"
              class="ml-25"
            />
          </b-button>

          <!-- Opret knap (kun på sidste step) -->
          <b-button
            v-if="state.currentStep === lastStep"
            variant="success"
            :disabled="isSubmitting || !isCurrentStepValid"
            @click="handleSubmit"
          >
            <b-spinner
              v-if="isSubmitting"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="CheckIcon"
              class="mr-25"
            />
            {{ $t('DEBTIA_CREATE_SUBMIT') }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Modal: Bekræft annullering -->
    <b-modal
      id="modal-cancel-confirm"
      :title="$t('DEBTIA_CREATE_CANCEL_TITLE')"
      ok-variant="danger"
      :ok-title="$t('DEBTIA_CREATE_CANCEL_CONFIRM')"
      :cancel-title="$t('DEBTIA_CREATE_CANCEL_DENY')"
      centered
      @ok="handleCancelConfirmed"
    >
      <p>{{ $t('DEBTIA_CREATE_CANCEL_QUESTION') }}</p>
      <p class="text-muted mb-0">
        {{ $t('DEBTIA_CREATE_CANCEL_DRAFT') }}
      </p>
    </b-modal>

    <!-- AI Scanning Overlay - Fullscreen dækker hele siden -->
    <AiScanningOverlay
      :is-visible="state.aiAnalysis.isAnalyzing"
      :current-index="state.aiAnalysis.completedCount + 1"
      :total-count="state.aiAnalysis.totalCount"
      :current-file-progress="state.aiAnalysis.currentFileProgress"
      :current-file-name="state.aiAnalysis.currentDocument"
      :files="state.aiAnalysis.scanningFiles"
    />
  </div>
</template>

<script>
/**
 * CreateCase.vue - Script
 *
 * Her håndteres:
 * - Integration med useCaseCreation composable
 * - Navigation mellem steps
 * - API-kald til oprettelse af sag
 * - Lifecycle hooks (mount, destroy)
 */
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BAlert,
  BSpinner,
  BModal,
} from 'bootstrap-vue'
import {
  computed,
  ref,
  onMounted,
} from '@vue/composition-api'
import axios from '@axios'
import constants from '@/app-configs/constants'

// Step komponenter - NY WIZARD STRUKTUR
import StepIndicator from './components/StepIndicator.vue'
import StepGrundlag from './components/StepGrundlag.vue'
import StepSkyldner from './components/StepSkyldner.vue'
import StepCustomVariables from './components/StepCustomVariables.vue'
import StepConfirmation from './components/StepConfirmation.vue'
import CaseSummaryBar from './components/CaseSummaryBar.vue'
import AiScanningOverlay from './components/AiScanningOverlay.vue'

// State management
import { useCaseCreation } from './composables/useCaseCreation'

export default {
  name: 'CreateCase',

  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BAlert,
    BSpinner,
    BModal,
    StepIndicator,
    StepGrundlag,
    StepSkyldner,
    StepCustomVariables,
    StepConfirmation,
    CaseSummaryBar,
    AiScanningOverlay,
  },

  watch: {
    'state.isLoading': {
      immediate: true,
      handler(val) {
        this.$store.commit('app/SET_GLOBAL_LOADING', { show: val, text: this.$t('DEBTIA_LOADING_CREATING_CASE') })
      },
    },
  },

  beforeDestroy() {
    this.$store.commit('app/SET_GLOBAL_LOADING', false)
  },

  setup(_, { root }) {
    // ============================================================
    // VALIDATION FUNCTIONS
    // ============================================================

    /**
     * Validerer en skyldner.
     */
    function validateDebtor(debtor) {
      if (!debtor.name || debtor.name.trim() === '') return false
      if (!debtor.debtorType) return false

      // CVR er påkrævet for virksomheder
      if (debtor.debtorType !== 'person' && (!debtor.cvr || debtor.cvr.length !== 8)) {
        return false
      }

      // Adresse er påkrævet
      if (!debtor.address.roadName || debtor.address.roadName.trim() === '') return false
      if (!debtor.address.houseNumber || debtor.address.houseNumber.trim() === '') return false
      if (!debtor.address.zipCode || debtor.address.zipCode.trim() === '') return false
      if (!debtor.address.city || debtor.address.city.trim() === '') return false

      return true
    }

    // ============================================================
    // COMPOSABLE & STATE
    // ============================================================

    // Hent alt fra vores composable - NY STRUKTUR
    const {
      state,
      STEPS,
      CASE_TYPES,
      DEBTOR_TYPES,
      isCurrentStepValid,
      hasCustomVariables,
      hasRequiredCustomVariables,
      hasAnyDocuments,
      totalReminderFee,
      totalCompensationFee,
      maxCompensationFee,
      nextStep,
      prevStep,
      goToStep,
      addDebtor,
      removeDebtor,
      initializeWizard,
      resetWizard,
    } = useCaseCreation()

    // Beregn sidste step (4 eller 3 hvis ingen custom vars)
    const lastStep = computed(() => (hasRequiredCustomVariables.value ? 4 : 4))

    // Lokal state
    const isSubmitting = ref(false)

    // Bruger-info fra localStorage (sættes ved login)
    // VIGTIGT: userData har nested struktur: { data: { id: ... } }
    const clientId = computed(() => {
      const userData = localStorage.getItem('userData')
      if (userData) {
        const parsed = JSON.parse(userData)
        return parsed.data?.id || parsed.id
      }
      return null
    })

    // ============================================================
    // API FUNCTIONS (defineret før event handlers der bruger dem)
    // ============================================================

    /**
     * Step 1: Gemmer sagstype og opretter sagen på serveren.
     * Returnerer debt_case_id som bruges i alle efterfølgende kald.
     */
    async function saveBaseType() {
      try {
        state.isLoading = true

        const response = await axios.post(`${constants.baseUrl}/save-base-type`, {
          base_type: state.caseType.selectedType,
          creditor_id: clientId.value,
          added_by: clientId.value,
          user_type: 'client',
        })

        if (response.status === 200) {
          // API returnerer 'id', ikke 'debt_case_id'
          state.debtCaseId = response.data.data.id
        }
      } catch (error) {
        state.error = error.response?.data?.message || 'Kunne ikke gemme sagstype'
        throw error
      } finally {
        state.isLoading = false
      }
    }

    /**
     * Step 1: Gemmer dokumenter - NY STRUKTUR med kategorier.
     */
    async function saveDocuments() {
      try {
        state.isLoading = true

        // Sikr at vi har et gyldigt debtCaseId
        if (!state.debtCaseId) {
          await saveBaseType()
        }

        // Gem grundlag dokumenter (fakturaer, domme, gældsbreve)
        if (state.documents.grundlag.length > 0) {
          const formData = new FormData()
          formData.append('debt_case_id', state.debtCaseId)

          const docsData = state.documents.grundlag.map((doc, idx) => ({
            recordIndex: idx + 1,
            title: doc.title,
            sentAt: doc.sentAt,
            dueDate: doc.dueDate,
            paymentAmount: doc.paymentAmount,
            vatAmount: doc.vatAmount || '',
            amountPaid: {
              status: !!(doc.amountPaid && parseFloat(doc.amountPaid) > 0),
              value: doc.amountPaid || '',
            },
            interestRate: {
              status: !!(doc.interestRate && parseFloat(doc.interestRate) > 0),
              value: doc.interestRate || '',
            },
          }))

          formData.append('data', JSON.stringify(docsData))

          state.documents.grundlag.forEach((doc, idx) => {
            if (doc.file) {
              formData.append(`basedocument_${idx + 1}`, doc.file)
            }
          })

          await axios.post(`${constants.baseUrl}/save-base-documents`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        }

        // Gem kreditnotaer
        if (state.documents.kreditnoter.length > 0) {
          const formData = new FormData()
          formData.append('debt_case_id', state.debtCaseId)

          const notesData = state.documents.kreditnoter.map((note, idx) => ({
            recordIndex: idx + 1,
            sentAt: note.sentAt,
            creditNoteAmount: note.creditNoteAmount,
          }))

          formData.append('data', JSON.stringify(notesData))

          state.documents.kreditnoter.forEach((note, idx) => {
            if (note.file) {
              formData.append(`creditnotes_${idx + 1}`, note.file)
            }
          })

          await axios.post(`${constants.baseUrl}/save-credit-notes`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        }

        // Gem rykkere
        if (state.documents.rykkere.length > 0) {
          const formData = new FormData()
          formData.append('debt_case_id', state.debtCaseId)
          formData.append('base_type', state.caseType.selectedType)

          const remindersData = state.documents.rykkere.map((r, idx) => ({
            recordIndex: idx + 1,
            documentDate: r.documentDate,
            dueDate: r.dueDate,
            reminderFee: Math.min(r.reminderFee || 0, 100), // Cap til 100 kr
            compensationFee: r.hasCompensationFee ? 310 : 0, // 310 kr hvis toggle er sat
            interestAmount: r.interestAmount || 0,
          }))

          formData.append('data', JSON.stringify(remindersData))

          state.documents.rykkere.forEach((r, idx) => {
            if (r.file) {
              formData.append(`debtreminders_${idx + 1}`, r.file)
            }
          })

          await axios.post(`${constants.baseUrl}/send-debt-reminders`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        }

        // Gem øvrig dokumentation
        if (state.documents.oevrig.length > 0) {
          const formData = new FormData()
          formData.append('debt_case_id', state.debtCaseId)

          const filesData = state.documents.oevrig.map((f, idx) => ({
            recordIndex: idx + 1,
            title: f.title,
            category_id: f.categoryId,
          }))

          formData.append('data', JSON.stringify(filesData))

          state.documents.oevrig.forEach((f, idx) => {
            if (f.file) {
              formData.append(`otherfiles_${idx + 1}`, f.file)
            }
          })

          await axios.post(`${constants.baseUrl}/upload-other-files`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        }
      } catch (error) {
        state.error = error.response?.data?.message || 'Kunne ikke gemme dokumenter'
        throw error
      } finally {
        state.isLoading = false
      }
    }

    /**
     * Step 2: Gemmer skyldnere.
     * Bruger FormData og /upsert-debtor endpoint (matcher CasesModal.vue format).
     */
    async function saveDebtors() {
      try {
        state.isLoading = true

        // Sikr at vi har et gyldigt debtCaseId
        if (!state.debtCaseId) {
          await saveBaseType()
        }

        // Send ALLE skyldnere (både nye og eksisterende) til upsert-debtor
        // Backend linker skyldnere til sagen - matcher CasesModal.vue
        const debtorsData = state.debtors.map((d, idx) => ({
          recordIndex: idx + 1,
          debtorID: d.debtorId || 0,
          name: {
            required: true,
            value: d.name || '',
          },
          co: {
            required: false,
            value: d.co || '',
          },
          bank_branch_no: {
            required: false,
            value: d.bankBranchNo || '',
          },
          bank_account_no: {
            required: false,
            value: d.bankAccountNo || '',
          },
          cpn: {
            required: false,
            value: d.cpn || '',
          },
          debtortype: {
            required: true,
            value: d.debtorType || '',
          },
          cpr: {
            required: false,
            value: d.debtorType === 'person' ? (d.cpr || '') : '',
          },
          cvrnumber: {
            required: d.debtorType !== 'person',
            value: d.debtorType !== 'person' ? (d.cvr || '') : '',
          },
          hide_address: false,
          address: {
            road_name: {
              required: true,
              value: d.address.roadName || '',
            },
            house_number: {
              required: true,
              value: d.address.houseNumber || '',
            },
            place: {
              required: false,
              value: d.address.place || '',
            },
            zip_code: {
              required: true,
              value: d.address.zipCode || '',
            },
            city: {
              required: true,
              value: d.address.city || '',
            },
            country_id: {
              required: true,
              value: d.address.countryId || 247,
            },
          },
          phone: {
            required: false,
            value: d.phone || '',
          },
          email: {
            required: false,
            value: d.email || '',
          },
        }))

        const form = new FormData()
        form.append('creditor_id', clientId.value)
        form.append('debt_case_id', state.debtCaseId)
        form.append('data', JSON.stringify(debtorsData))

        const response = await axios.post(`${constants.baseUrl}/upsert-debtor`, form)

        // Gem returnerede debtor IDs
        if (response.data?.data?.debtors) {
          response.data.data.debtors.forEach((returnedDebtor, idx) => {
            if (state.debtors[idx]) {
              state.debtors[idx].debtorId = returnedDebtor.id
              state.debtors[idx].isExisting = true
            }
          })
        }
      } catch (error) {
        state.error = error.response?.data?.message || 'Kunne ikke gemme skyldnere'
        throw error
      } finally {
        state.isLoading = false
      }
    }

    /**
     * Step 5: Gemmer custom variables.
     */
    async function saveCustomVariables() {
      if (state.customVariables.values.length === 0) return

      try {
        state.isLoading = true

        // Sikr at vi har et gyldigt debtCaseId
        if (!state.debtCaseId) {
          await saveBaseType()
        }

        await axios.post(`${constants.serverBaseUrl}/api/v2/debtiamit/case/save-custom-variables`, {
          debt_case_id: state.debtCaseId,
          variables: state.customVariables.values,
        })
      } catch (error) {
        state.error = error.response?.data?.message || 'Kunne ikke gemme generel information'
        throw error
      } finally {
        state.isLoading = false
      }
    }

    // ============================================================
    // EVENT HANDLERS
    // ============================================================

    /**
     * Håndterer klik på step-indikatoren.
     * Tillader kun at gå til steps man har gennemført.
     */
    function handleStepClick(stepNumber) {
      goToStep(stepNumber)
    }

    /**
     * Håndterer "Næste" knappen.
     * NY LOGIK for 3-4 steps wizard.
     */
    async function handleNextStep() {
      try {
        // Step 1 (Grundlag): Gem sagstype og dokumenter
        if (state.currentStep === 1) {
          // Bestem sagstype baseret på AI-analyse eller default til faktura
          if (!state.caseType.selectedType) {
            state.caseType.selectedType = 1 // Default: Faktura
          }
          await saveBaseType()
          await saveDocuments()
        }

        // Step 2 (Skyldner): Gem skyldnere til API
        if (state.currentStep === 2) {
          await saveDebtors()
        }

        // Step 3 (Ekstra information): Gem custom variables
        if (state.currentStep === 3 && hasRequiredCustomVariables.value) {
          await saveCustomVariables()
        }

        // Gå til næste step
        nextStep()
      } catch (error) {
        // Fejlen er allerede sat i state.error af de individuelle save-funktioner
        console.error('Fejl ved gem af step data:', error)
      }
    }

    /**
     * Håndterer opdatering af en skyldner.
     */
    function handleDebtorUpdate(debtorId, field, value) {
      const debtor = state.debtors.find(d => d.id === debtorId)
      if (debtor) {
        // Håndter nested fields (f.eks. 'address.roadName')
        if (field.includes('.')) {
          const [parent, child] = field.split('.')
          debtor[parent][child] = value
        } else {
          debtor[field] = value
        }
        debtor.isValid = validateDebtor(debtor)
        state.hasUnsavedChanges = true
      }
    }

    /**
     * Håndterer opdatering af custom variable.
     */
    function handleCustomVariableUpdate(variableName, value) {
      const existingIndex = state.customVariables.values.findIndex(
        v => v.customVarLabel === variableName,
      )

      if (existingIndex > -1) {
        state.customVariables.values[existingIndex].value = value
      } else {
        state.customVariables.values.push({
          customVarLabel: variableName,
          value,
        })
      }
      state.hasUnsavedChanges = true
    }

    /**
     * Håndterer endelig indsendelse af sagen.
     */
    async function handleSubmit() {
      try {
        isSubmitting.value = true
        state.isLoading = true

        // Opret sagen endeligt
        await axios.post(`${constants.baseUrl}/create-debt-case`, {
          status: 1,
          debt_case_id: state.debtCaseId,
        })

        // Nulstil wizard state så næste oprettelse starter forfra
        resetWizard()

        // Vis succesbesked og naviger til sager
        root.$bvToast.toast(root.$t('DEBTIA_TOAST_CASE_CREATED'), {
          title: root.$t('DEBTIA_COMMON_SUCCESS'),
          variant: 'success',
          solid: true,
        })

        // Naviger til sagslisten
        root.$router.push({ name: 'cases' })
      } catch (error) {
        state.error = error.response?.data?.message || 'Kunne ikke oprette sagen'
      } finally {
        isSubmitting.value = false
        state.isLoading = false
      }
    }

    /**
     * Bekræft annullering med modal.
     */
    function confirmCancel() {
      root.$bvModal.show('modal-cancel-confirm')
    }

    /**
     * Håndterer bekræftet annullering.
     */
    function handleCancelConfirmed() {
      root.$router.push({ name: 'cases' })
    }

    // ============================================================
    // LIFECYCLE HOOKS
    // ============================================================

    /**
     * Når komponenten mountes:
     * - Initialiser wizard
     * - Hent custom variables fra API
     */
    onMounted(async () => {
      // Initialiser med tomme data
      initializeWizard()

      // Hent custom variables fra API
      try {
        const response = await axios.get(
          `${constants.serverBaseUrl}/api/v2/debtiamit/case/get-custom-variables`,
        )
        if (response.data.data?.variables) {
          state.customVariables.definitions = response.data.data.variables
        }
      } catch (err) {
        console.log('Ingen custom variables konfigureret')
      }
    })

    // ============================================================
    // RETURN
    // ============================================================

    return {
      // State
      state,
      isSubmitting,
      clientId,

      // Constants
      STEPS,
      CASE_TYPES,
      DEBTOR_TYPES,

      // Computed
      isCurrentStepValid,
      hasCustomVariables,
      hasRequiredCustomVariables,
      hasAnyDocuments,
      totalReminderFee,
      totalCompensationFee,
      maxCompensationFee,
      lastStep,

      // Navigation
      prevStep,
      handleNextStep,
      handleStepClick,

      // Debtor handlers
      addDebtor,
      removeDebtor,
      handleDebtorUpdate,

      // Custom variable handler
      handleCustomVariableUpdate,

      // Actions
      handleSubmit,
      confirmCancel,
      handleCancelConfirmed,
    }
  },
}
</script>

<style lang="scss" scoped>
/**
 * Styles for CreateCase
 *
 * Vi holder styles minimale og bruger Bootstrap-Vue's
 * built-in classes så meget som muligt.
 */

.create-case-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.step-content-card {
  min-height: 400px;
}

// Responsive justeringer
@media (max-width: 768px) {
  .create-case-container {
    padding: 0.5rem;
  }
}
</style>
